import type { StaticContext } from 'react-router';
import type { RouteComponentProps } from 'react-router-dom';
import type { AuthTokensResponse } from '../../types/identityAPI';

import Environments, { APIs } from '@delta-defense/client-environments';
import { BrandId, getBrandIdFromHostname } from '@delta-defense/delta-utils';
import React, { useEffect, useState } from 'react';
import * as z from 'zod/v4';

import { CookieService } from '../../core/services/cookieService';
import { getOneWeekFromDate, getOneYearFromDate } from '../../utilities/getAuthTTL';
import { getPlatform } from '../../utilities/getPlatform';

import { getLoginMethod } from '../../utilities/getLoginMethod';
import Question from '../AdditionalInfo/Question';
import AuthButton from '../AuthButton/AuthButton';
import { DefaultErrorPage } from '../ErrorPage/defaultErrorPage';
import { InputGroup } from '../InputGroup/InputGroup';
import Loading from '../Loading/Loading';
import SignInLinks from '../NavigationLinks/SignInLinks';
import { LoadingSpinner } from '../RedirectChecker/RedirectChecker';
import Title from '../Title/Title';

type Props = RouteComponentProps<{}, StaticContext, unknown>

const OK_STATUS = 200;
const EMAIL_NOT_FOUND_STATUS = 404;
const WRONG_PASSWORD_STATUS = 422;

const handledStatusCodes = [
  OK_STATUS,
  EMAIL_NOT_FOUND_STATUS,
  WRONG_PASSWORD_STATUS
];

export function LogIn(props: Props): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [loading] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [statusCode, setStatusCode] = useState<number | null>(null);

  const brandID = getBrandIdFromHostname();
  const cookieService = CookieService.Instance();
  const isVerification = new URLSearchParams(window.location.search).has('verification');

  const pageTitle = isVerification ? 'Member Verification' : 'Log In';

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const queryEmail = queryParams.get('email');

    if (queryEmail) {
      setEmail(queryEmail);
    }
  }, []);

  function getTTL(rememberMe: boolean): string {
    const today = new Date();

    return rememberMe
      ? getOneYearFromDate(today)
      : getOneWeekFromDate(today);
  }

  function validateEmailAddress(email: string): string | void {
    try {
      z.email().parse(email);
    } catch (error) {
      if (error instanceof z.ZodError) {
        return 'Please enter a valid email address';
      }
    }
  }

  function validatePassword(password: string): string | void {
    try {
      z.string().min(1).parse(password);
    } catch (error) {
      if (error instanceof z.ZodError) {
        return 'Password is required';
      }
    }
  }

  function clearAllErrors(): void {
    setEmailError(null);
    setPasswordError(null);
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    clearAllErrors();

    const emailErrorMessage = validateEmailAddress(email);
    if (emailErrorMessage) {
      setEmailError(emailErrorMessage);
      return;
    }

    const passwordErrorMessage = validatePassword(password);
    if (passwordErrorMessage) {
      setPasswordError(passwordErrorMessage);
      return;
    }

    const authEndpoint = `${Environments.getProxyEndpointForApi(APIs.Auth)}/tokens`;
    const authBody = JSON.stringify({
      email,
      password,
      ttl: getTTL(rememberMe),
      meta: {
        ...(window.deltaAnalytics?.aggregateAvailableProperties() || {}),
        email,
        referrer: document.referrer,
        method: getLoginMethod(),
        platform: getPlatform()
      }
    });

    const response = await fetch(authEndpoint, {
      method: 'POST',
      body: authBody,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const body: AuthTokensResponse = await response.json();

    if (response.status === EMAIL_NOT_FOUND_STATUS) {
      setEmailError('There is no account for the email you entered');
      return;
    } else if (response.status === WRONG_PASSWORD_STATUS) {
      setPasswordError('There was an error when entering your password');
      return;
    } else if (body.errors) {
      setStatusCode(response.status);
      return;
    }

    if (body.data) {
      const { token, ttl } = body.data.attributes;
      await cookieService.setAccessToken(token, ttl);
      props.history.push(`/redirect${window.location.search}`);
    }
  }

  if (statusCode && !handledStatusCodes.includes(statusCode)) {
    return <DefaultErrorPage errorStatus={statusCode} />;
  } else if (statusCode === OK_STATUS) {
    return <LoadingSpinner />;
  } else {
    return (
      <>
        <form name='signin' onSubmit={handleSubmit}>
          <Title name={pageTitle} />
          {isVerification && (<p className="description">Log in to verify your USCCA membership status</p>)}

          <InputGroup
            id='email'
            name='email'
            type='email'
            label='Email'
            autoComplete='email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            error={emailError}
            autoFocus={true}
          />

          <InputGroup
            id='password'
            name='password'
            type='password'
            label='Password'
            autoComplete='current-password'
            value={password}
            onChange={e => setPassword(e.target.value)}
            error={passwordError}
          />

          <div className="remember-section">
            <div className="form-check">
              <label htmlFor='remember' className='form-check-label remember-label'>
                Remember Me
                <input
                  type="checkbox"
                  name="remember"
                  id="remember"
                  className='form-check-input'
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                />
                <span className="checkmark"></span>
              </label>
            </div>

            <SignInLinks />
          </div>

          <AuthButton
            id="signInSignInButton"
            color="btn-gold"
            text={loading ? <Loading /> : 'Log In Now'}
            disabled={loading}
          />
        </form>

        {brandID === BrandId.USCCA && (
          <Question
            headline="Don't have an account?"
            link={`/register${window.location.search}`}
            text="Sign Up Today"
            id="signUpTodayButton"
          />
        )}
      </>
    );
  }
}
